import Fuse from 'fuse.js';
import { useMemo } from 'react';

import type { AnyMenuNode } from './tree';

const fuseParameters = {
    threshold: 0.3,
    location: 0,
    distance: 100,
    shouldSort: true,
    includeScore: true,
    minMatchCharLength: 1,
    isCaseSensitive: false,
    // Weight title higher than subtitle/keywords so direct title matches rank first
    keys: [
        { name: 'title', weight: 0.7 },
        { name: 'keywords', weight: 0.5 },
        { name: 'subtitle', weight: 0.3 },
    ],
};

function getNodeChildren(node: AnyMenuNode) {
    if (node.type === 'item' && 'children' in node && node.children) {
        return node.children
            .map((node) => {
                if ('parent' in node && node.parent) {
                    return {
                        node,
                        title: [node.parent.title, node.title].join(' > '),
                    };
                }
                return null;
            })
            .filter((x) => x !== null);
    }

    return [];
}

/**
 * Filter menu nodes using Fuse.
 */
export function useFilterMenuNodes(nodes: AnyMenuNode[], query: string | null): AnyMenuNode[] {
    const topLevelFuse = useMemo(() => {
        return new Fuse(nodes, fuseParameters);
    }, [nodes]);
    const nestedFuse = useMemo(() => {
        return new Fuse(nodes.flatMap(getNodeChildren), {
            ...fuseParameters,
            keys: [
                { name: 'title', weight: 0.7 },
                { name: 'node.keywords', weight: 0.5 },
                { name: 'node.subtitle', weight: 0.3 },
            ],
        });
    }, [nodes]);
    return useMemo(
        () =>
            query
                ? [
                      ...topLevelFuse.search(query).map((r) => r.item),
                      ...nestedFuse.search(query).map((r) => r.item.node),
                  ]
                : nodes,
        [topLevelFuse, nestedFuse, nodes, query]
    );
}
